import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  ContentContainer,
  Section,
  SectionHeader,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  ButtonContainer,
  EditButton,
  DeleteButton,
} from './style'
import { BlurBackground } from '../style'
import cross from '../../../../assets/addStockModal/cross.svg'
import editIcon from '../../../../assets/edit-pen-white.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import { getTagTextColor } from '../../../../utils/colorContrast'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-product`

interface ViewCollectionModalProps {
  collectionId: number
  onClose: () => void
}

interface CollectionDetailItem {
  label: string
  value: string | React.ReactNode
}

interface ThemeItem {
  id: number
  name: string
  color?: string | null
}

interface FullCollectionData {
  general: CollectionDetailItem[]
  families: CollectionDetailItem[]
  grades: CollectionDetailItem[]
  groups: CollectionDetailItem[]
  materials: CollectionDetailItem[]
  colors: CollectionDetailItem[]
  themes: ThemeItem[]
}

const ViewCollectionModal: React.FC<ViewCollectionModalProps> = ({
  collectionId,
  onClose,
}) => {
  const navigate = useNavigate()
  const { setFeedback } = useContext(FeedbackContext)

  const [collectionData, setCollectionData] =
    useState<FullCollectionData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)

  useEffect(() => {
    const fetchCollectionData = async () => {
      if (!collectionId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(
          `${API_BASE_URL}/collections/${collectionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        const data = response.data

        const mappedGeneral: CollectionDetailItem[] = [
          { label: 'ID', value: data.id?.toString() || 'N/A' },
          { label: 'Nome da Coleção', value: data.name || 'N/A' },
        ]

        let mappedFamilies: CollectionDetailItem[] = []
        try {
          const familiesResponse = await axios.get(
            `${API_BASE_URL}/collections/${collectionId}/families`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          )
          const familiesData = familiesResponse.data
          const familyNames = Array.isArray(familiesData) && familiesData.length > 0
            ? familiesData.map((f: any) => f.name).join(', ')
            : 'Nenhuma família cadastrada'
          mappedFamilies = [{ label: 'Famílias', value: familyNames }]
        } catch (err) {
          mappedFamilies = [{ label: 'Famílias', value: 'Nenhuma família encontrada' }]
        }

        const gradesByType: Record<string, Set<string>> = {}
        ;(data.GradeInCollection || []).forEach((g: any) => {
          const typeName = g.Grade?.GraduationType?.name || g.Grade?.type?.name || 'Sem tipo'
          const gradeName = g.Grade?.name
          if (!gradeName) return
          if (!gradesByType[typeName]) gradesByType[typeName] = new Set()
          gradesByType[typeName].add(gradeName)
        })

        const gradeLines = Object.entries(gradesByType).map(([type, grades]) => {
          const arr = Array.from(grades)
          return (
            <span key={type}>
              <span style={{ fontWeight: 600 }}>{type}:</span>
              <span style={{ fontWeight: 400 }}> {arr.join(', ')}</span>
            </span>
          )
        })

        const mappedGrades: CollectionDetailItem[] = [
          {
            label: 'Grades',
            value:
              gradeLines.length > 0 ? (
                <span>
                  {gradeLines.reduce<React.ReactNode[]>((acc, line, idx) => {
                    if (idx > 0) acc.push(' | ')
                    acc.push(line)
                    return acc
                  }, [])}
                </span>
              ) : (
                'Nenhuma grade'
              ),
          },
        ]

        const groupNames = (data.GroupInCollection || [])
          .map((g: any) => g.Group?.name)
          .filter(Boolean)
        const mappedGroups: CollectionDetailItem[] = [
          { label: 'Grupos', value: groupNames.length > 0 ? groupNames.join(', ') : 'Nenhum grupo' },
        ]

        const materialsWithAviamentos = (data.CollectionMaterial || []).map((cm: any, idx: number) => {
          const materialName = cm.Material?.name || (cm.materialId ? `Material #${cm.materialId}` : `Tecido ${idx + 1}`)

          const byCategory: Record<string, Set<string>> = {}
          ;(cm.CollectionMaterialResources || []).forEach((r: any) => {
            const categoryName = r.Category_Resource?.name || 'Sem categoria'
            const linkedMaterialName =
              r.Material?.name ||
              (r.materialId ? `Material #${r.materialId}` : undefined) ||
              (Array.isArray(r.resources) && r.resources.length > 0
                ? r.resources.map((res: any) => res?.name).filter(Boolean)[0]
                : undefined) ||
              `Registro #${r.id}`

            if (!byCategory[categoryName]) byCategory[categoryName] = new Set()
            if (linkedMaterialName) byCategory[categoryName].add(linkedMaterialName)
          })

          const categoryLines = Object.entries(byCategory).map(([cat, names]) => {
            const unique = Array.from(names)
            return (
              <span key={cat}>
                <span style={{ fontWeight: 600 }}>{cat}:</span>
                <span style={{ fontWeight: 400 }}> {unique.join(', ')}</span>
              </span>
            )
          })

          return {
            label: materialName,
            value: categoryLines.length > 0 ? (
              <span>
                {categoryLines.reduce<React.ReactNode[]>((acc, line, idx) => {
                  if (idx > 0) acc.push(' | ')
                  acc.push(line)
                  return acc
                }, [])}
              </span>
            ) : (
              'Nenhum aviamento'
            ),
          }
        })

        const mappedColors: CollectionDetailItem[] = (data.CollectionColor || []).map((cc: any) => {
          const pantone = cc.Pantone || {}
          const hex = pantone.hex || cc.hex
          return {
            label: cc.name || 'Cor sem nome',
            value: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {hex && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      backgroundColor: hex.startsWith('#') ? hex : `#${hex}`,
                      border: '1px solid rgba(0,0,0,0.15)',
                    }}
                    title={hex}
                  />
                )}
                <span style={{ fontWeight: 400 }}>
                  {pantone.code ? `Pantone: ${pantone.code}` : 'Sem Pantone'}
                </span>
              </div>
            )
          }
        })

        const mappedThemes: ThemeItem[] = (data.collectionThemes || [])
          .map((ct: any) => ct.Theme)
          .filter(Boolean)
          .map((t: any) => ({ id: t.id, name: t.name, color: t.color || null }))

        setCollectionData({
          general: mappedGeneral,
          families: mappedFamilies,
          grades: mappedGrades,
          groups: mappedGroups,
          materials: materialsWithAviamentos,
          colors: mappedColors,
          themes: mappedThemes,
        })
        setLoading(false)
      } catch (err: any) {
        console.error('Erro ao buscar detalhes da coleção:', err)
        setError(
          err.response?.data?.message ||
            'Erro ao carregar dados da coleção. Tente novamente.',
        )
        setLoading(false)
      }
    }

    fetchCollectionData()
  }, [collectionId])

  const handleClose = () => {
    onClose()
  }

  const handleEditCollection = () => {
    navigate(`/panel/collection/edit/${collectionId}`)
    onClose()
  }

  const handleDeleteCollection = async () => {
    setShowDeleteConfirm(true)
  }

  const handleConfirmDeleteCollection = async () => {

    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({ isOpen: true, statusOk: false, message: 'Sessão expirada. Faça login novamente.' })
      setShowDeleteConfirm(false)
      return
    }

    try {
      await axios.delete(`${API_BASE_URL}/collections/${collectionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setFeedback({ isOpen: true, statusOk: true, message: 'Coleção excluída com sucesso!' })
      setShowDeleteConfirm(false)
      onClose()
      window.location.reload()
    } catch (err: any) {
      console.error('Erro ao excluir coleção:', err)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: err.response?.data?.message || 'Erro ao excluir coleção. Tente novamente.',
      })
    }
  }

  const handleCancelDeleteCollection = () => {
    setShowDeleteConfirm(false)
  }

  if (loading) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Carregando dados da coleção...</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Carregando informações da coleção...</p>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (error) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Erro ao carregar dados</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>
              <button onClick={handleClose}>Fechar</button>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (!collectionData) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Coleção não encontrada</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Nenhum dado encontrado para a coleção com ID: {collectionId}.
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  const collectionNameItem = collectionData.general.find(
    (item) => item.label === 'Nome da Coleção',
  )
  const displayName = collectionNameItem
    ? collectionNameItem.value
    : `Coleção #${collectionId}`

  return (
    <>
    <BlurBackground>
      <Container>
        <HeaderDiv>
          <HeaderText>Coleção: {displayName}</HeaderText>
          <CloseDiv onClick={handleClose}>
            <img src={cross} alt='Fechar' />
          </CloseDiv>
        </HeaderDiv>

        <ContentContainer>
          <Section>
            <SectionHeader>Dados gerais</SectionHeader>
            <DetailGrid>
              {collectionData.general.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Famílias</SectionHeader>
            <DetailGrid>
              {collectionData.families.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Grades</SectionHeader>
            <DetailGrid>
              {collectionData.grades.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Grupos</SectionHeader>
            <DetailGrid>
              {collectionData.groups.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Temas</SectionHeader>
            {collectionData.themes.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '4px 0' }}>
                {collectionData.themes.map((theme) => {
                  const themeColor = theme.color || '#e0e0e0'
                  const themeTextColor = getTagTextColor(theme.color || undefined)
                  const shouldUseSolidThemeBackground = themeTextColor === '#FFFFFF'

                  return (
                    <span
                      key={theme.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: 500,
                        backgroundColor: shouldUseSolidThemeBackground
                          ? themeColor
                          : theme.color
                          ? `${theme.color}18`
                          : '#f3f4f6',
                        border: `1px solid ${themeColor}`,
                        color: shouldUseSolidThemeBackground ? '#FFFFFF' : '#333',
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: theme.color || '#999',
                          border: '1px solid rgba(0,0,0,0.1)',
                          flexShrink: 0,
                        }}
                      />
                      {theme.name}
                    </span>
                  )
                })}
              </div>
            ) : (
              <DetailGrid>
                <DetailItem>
                  <DetailValue>Nenhum tema vinculado</DetailValue>
                </DetailItem>
              </DetailGrid>
            )}
          </Section>

          <Section>
            <SectionHeader>Tecidos e Aviamentos</SectionHeader>
            <DetailGrid>
              {collectionData.materials.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          {collectionData.colors && collectionData.colors.length > 0 && (
            <Section>
              <SectionHeader>Cores da Coleção</SectionHeader>
              <DetailGrid>
                {collectionData.colors.map((color, index) => (
                  <DetailItem key={index}>
                    <DetailLabel>{color.label}</DetailLabel>
                    <DetailValue>{color.value}</DetailValue>
                  </DetailItem>
                ))}
              </DetailGrid>
            </Section>
          )}
        </ContentContainer>

        <ButtonContainer>
          <DeleteButton onClick={handleDeleteCollection}>
            <img src={trash} alt='Excluir' />
            Excluir coleção
          </DeleteButton>
          <EditButton style={{ marginLeft: '1rem' }} onClick={handleEditCollection}>
            <img src={editIcon} alt='Editar' />
            Editar coleção
          </EditButton>
        </ButtonContainer>
      </Container>
    </BlurBackground>
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title='Confirmar exclusão'
        message='Tem certeza que deseja excluir esta coleção? Esta ação não pode ser desfeita.'
        actions={[
          { label: 'Cancelar', onClick: handleCancelDeleteCollection, variant: 'outline' },
          { label: 'Excluir', onClick: handleConfirmDeleteCollection, variant: 'danger' },
        ]}
      />
    </>
  )
}

export default ViewCollectionModal